import { Badge, Group, Text } from "@mantine/core";
import { IconArrowUp, IconArrowDown } from "@tabler/icons-react";
import {
  formatPrice,
  formatPercentChange,
  getChangeColor,
  type ChangeType,
} from "../utils/price";

interface PriceBadgeProps {
  /** Price in USD as a JS number. Backend sends Decimal; safe for display at 2dp. */
  currentPrice: number;
  previousPrice?: number | null;
  changeType?: ChangeType;
  /** Percentage as a whole number, e.g. 3.8 for 3.8%, NOT 0.038. */
  percentChange?: number | null;
  size?: "sm" | "md" | "lg";
}

const SIZE_MAP = {
  sm: { price: "sm", badge: "sm", icon: 12 },
  md: { price: "md", badge: "md", icon: 14 },
  lg: { price: "lg", badge: "lg", icon: 16 },
} as const;

function ChangeArrow({
  changeType,
  iconSize,
}: {
  changeType: ChangeType;
  iconSize: number;
}) {
  if (changeType === "price_increase")
    return <IconArrowUp size={iconSize} stroke={2.5} aria-hidden="true" />;
  if (changeType === "price_decrease")
    return <IconArrowDown size={iconSize} stroke={2.5} aria-hidden="true" />;
  return null;
}

export function PriceBadge({
  currentPrice,
  previousPrice,
  changeType,
  percentChange,
  size = "md",
}: PriceBadgeProps) {
  const s = SIZE_MAP[size];

  // Derive changeType from price delta if not provided.
  // Treat previousPrice of 0 or null as "no change" — 0 means OOS/free, not a real price.
  const resolvedChangeType: ChangeType =
    changeType ??
    (previousPrice != null && previousPrice !== 0 && currentPrice > previousPrice
      ? "price_increase"
      : previousPrice != null && previousPrice !== 0 && currentPrice < previousPrice
        ? "price_decrease"
        : "no_change");

  const color = getChangeColor(resolvedChangeType);
  const showBadge = resolvedChangeType !== "no_change";
  const badgeText =
    percentChange != null
      ? formatPercentChange(percentChange)
      : resolvedChangeType.replace(/_/g, " ");

  return (
    <Group gap={6} wrap="nowrap">
      <Text fw={600} size={s.price}>
        {formatPrice(currentPrice)}
      </Text>
      {showBadge && (
        <Badge
          color={color}
          size={s.badge}
          variant="light"
          aria-label={`${resolvedChangeType.replace(/_/g, " ")}: ${badgeText}`}
          leftSection={
            <ChangeArrow
              changeType={resolvedChangeType}
              iconSize={s.icon}
            />
          }
        >
          {badgeText}
        </Badge>
      )}
    </Group>
  );
}
